import { useCallback, useEffect, useState } from 'react';

/** Runtime guard applied to every value read back from sessionStorage. */
export type SessionIdGuard<T extends string | number> = (value: unknown) => value is T;

/** Guard for numeric ids (e.g. league ids). */
export const isIntegerId: SessionIdGuard<number> = (value): value is number =>
  Number.isInteger(value);

/** Guard for string keys (e.g. country group keys). */
export const isStringId: SessionIdGuard<string> = (value): value is string =>
  typeof value === 'string';

function readIds<T extends string | number>(key: string, isValid: SessionIdGuard<T>): Set<T> {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter(isValid)) : new Set();
  } catch {
    // Storage disabled (private mode, sandboxed WebView) or corrupted value.
    return new Set();
  }
}

export interface SessionIdSet<T extends string | number> {
  ids: ReadonlySet<T>;
  toggle: (id: T) => void;
  /** Adds (`include = true`) or removes every id in `targets` at once. */
  setMany: (targets: readonly T[], include: boolean) => void;
}

/**
 * A set of ids of one type (numbers or strings) that survives re-mounts and
 * reloads within the browser tab (sessionStorage). Stored values failing
 * `isValid` are dropped on read. Storage failures degrade to in-memory state.
 */
export function useSessionIdSet<T extends string | number>(
  key: string,
  isValid: SessionIdGuard<T>,
): SessionIdSet<T> {
  const [ids, setIds] = useState<Set<T>>(() => readIds(key, isValid));

  useEffect(() => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify([...ids]));
    } catch {
      // Quota exceeded or storage disabled — keep the in-memory state only.
    }
  }, [ids, key]);

  const toggle = useCallback((id: T) => {
    setIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setMany = useCallback((targets: readonly T[], include: boolean) => {
    setIds(prev => {
      const next = new Set(prev);
      for (const id of targets) {
        if (include) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  return { ids, toggle, setMany };
}
